/**
 * Auth endpoints, matching api/app/routers/auth.py.
 *
 * The access token lives in memory only. The refresh token is an httpOnly
 * cookie the browser never exposes to script, and it is bound to a CSRF token
 * the client has to echo in a header, because the refresh endpoint is driven by
 * a cookie the browser attaches on its own. The predecessor kept its access
 * token in localStorage, so any injected script on the page could lift an hour
 * of authority over a fleet of armed aircraft.
 */

import { api, type ApiResult } from '@/api/client';
import type { DroneId, Role, Team, User } from '@/types/domain';

export const CSRF_HEADER = 'X-CSRF-Token';

/** What /auth/login and /auth/refresh return. */
export interface TokenGrant {
  accessToken: string;
  /** Seconds until the access token expires. */
  expiresIn: number;
  csrfToken: string;
  user: User;
}

/** What /auth/me returns: identity plus the drone scope, but no token. */
export interface Identity {
  user: User;
  teams: Team[];
  /**
   * True for a role whose scope has no membership list. An admin gets this flag
   * and asks the vehicle registry instead, because "every drone" is not a set.
   */
  seesAllDrones: boolean;
  accessibleDroneIds: DroneId[] | null;
  controllableDroneIds: DroneId[] | null;
  serverTime: number;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export function login(credentials: LoginCredentials): Promise<ApiResult<TokenGrant>> {
  return api.post<TokenGrant>('/auth/login', credentials, { label: 'sign in' });
}

/**
 * Exchange the refresh cookie for a new access token.
 *
 * Not logged: this runs on a timer well before expiry, and a line every fifty
 * minutes saying nothing happened is noise in a log an operator reads during an
 * incident.
 */
export function refresh(csrfToken: string): Promise<ApiResult<TokenGrant>> {
  return api.post<TokenGrant>('/auth/refresh', undefined, {
    headers: { [CSRF_HEADER]: csrfToken },
    logged: false,
  });
}

export function logout(): Promise<ApiResult<{ ok: boolean }>> {
  return api.post<{ ok: boolean }>('/auth/logout', undefined, { label: 'sign out' });
}

export function me(): Promise<ApiResult<Identity>> {
  return api.get<Identity>('/auth/me', { logged: false });
}

export function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<ApiResult<{ ok: boolean }>> {
  return api.post<{ ok: boolean }>(
    '/auth/change-password',
    { current_password: currentPassword, new_password: newPassword },
    { label: 'change password' },
  );
}

export interface RoleDescription {
  role: Role;
  label: string;
  summary: string;
}

/** What each role may do, in the words shown to an operator. */
export const ROLE_DESCRIPTIONS: readonly RoleDescription[] = [
  { role: 'viewer', label: 'Viewer', summary: 'Sees assigned aircraft. Sends nothing.' },
  { role: 'operator', label: 'Operator', summary: 'Flies assigned aircraft.' },
  { role: 'tactical', label: 'Tactical', summary: 'Flies and plans across the team.' },
  {
    role: 'group_commander',
    label: 'Group Commander',
    summary: 'Runs several teams: their ports, their people up to Tactical, and every aircraft in them.',
  },
  { role: 'admin', label: 'Admin', summary: 'Full fleet, users and teams.' },
];
